import { useCallback, useState } from "react";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SelectDeviceCard } from "@/components/select-device-card";
import { proxyImageUrl } from "@/lib/snipeit/client";
import type { SnipeItAsset, SnipeItLocation } from "@/lib/snipeit/types";
import { useVerwerterLabel } from "@/common/api/use-verwerter-label";
import { useLocationDetail } from "@/common/api/use-location-detail";
import { useCheckinHardware } from "@/common/api/use-checkin-hardware";
import { Link } from "wouter";

export function isAvailableForCheckin(asset: SnipeItAsset, location: SnipeItLocation | null): boolean {
  return !(location && asset.location?.id === location.id);
}

export function CheckinDeviceView() {
  const { location: savedLocation } = useLocationDetail();
  const [selected, setSelected] = useState<SnipeItAsset | null>(null);
  const [verwerterDialogOpen, setVerwerterDialogOpen] = useState(false);

  const { data: verwerterLabel } = useVerwerterLabel();

  const imageUrl = proxyImageUrl(selected?.image);

  const checkinMutation = useCheckinHardware();

  const isLoadingCheckin = checkinMutation.isPending && checkinMutation.variables?.statusId === undefined;
  const isLoadingVerwerter = checkinMutation.isPending && checkinMutation.variables?.statusId !== undefined;
  const isLoading = checkinMutation.isPending;

  const filterAsset = useCallback(
    (asset: SnipeItAsset) => isAvailableForCheckin(asset, savedLocation),
    [savedLocation],
  );

  return (
    <main className="bg-background flex min-h-dvh items-start justify-center overflow-y-auto px-4 pt-6 pb-6 md:h-dvh md:overflow-hidden">
      <div className="flex w-full max-w-3xl flex-col gap-3">
        {!savedLocation && (
          <div className="border-border bg-card rounded-xl border p-6 text-center shadow-sm">
            <p className="text-muted-foreground text-sm">
              Bitte zuerst einen Standort in den{" "}
              <Link href="/settings" className="text-primary underline">
                Einstellungen
              </Link>{" "}
              festlegen.
            </p>
          </div>
        )}

        {savedLocation && (
          <div className="flex w-full flex-col items-start gap-6 md:flex-row md:overflow-hidden">
            <div className="w-full shrink-0 space-y-3 md:w-80">
              <SelectDeviceCard
                queryKeyPrefix="checkin"
                statusType="deployed"
                filterFn={filterAsset}
                onSelect={(asset) => {
                  setSelected(asset);
                  checkinMutation.reset();
                }}
              />

              {checkinMutation.isError && (
                <p className="text-destructive text-sm">
                  {checkinMutation.error instanceof Error ? checkinMutation.error.message : "Unbekannter Fehler"}
                </p>
              )}

              <div className="flex flex-wrap items-center justify-between gap-2">
                <Link href="/" className={buttonVariants({ variant: "secondary", size: "sm" })}>
                  Zurück
                </Link>
                <div className="flex w-full flex-wrap justify-end gap-2 md:w-auto">
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={!selected || !verwerterLabel || !savedLocation || isLoading}
                    onClick={() => setVerwerterDialogOpen(true)}
                  >
                    {isLoadingVerwerter ? "Wird verarbeitet…" : "Verwerter"}
                  </Button>
                  <Button
                    size="sm"
                    disabled={!selected || !savedLocation || isLoading}
                    onClick={() => {
                      if (!selected) return;
                      checkinMutation.mutate({ assetId: selected.id, locationId: savedLocation?.id });
                    }}
                  >
                    {isLoadingCheckin ? "Wird verarbeitet…" : "Zurücknehmen"}
                  </Button>
                </div>
              </div>

              <AlertDialog open={verwerterDialogOpen} onOpenChange={setVerwerterDialogOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Gerät an Verwerter übergeben?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Das Gerät <strong>{selected?.name ?? selected?.asset_tag}</strong> wird als
                      &bdquo;Verwerter&ldquo; markiert. Diese Aktion kann nicht rückgängig gemacht werden.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() => {
                        if (!selected || !verwerterLabel) return;
                        checkinMutation.mutate({
                          assetId: selected.id,
                          locationId: savedLocation?.id,
                          statusId: verwerterLabel.id,
                        });
                      }}
                    >
                      Bestätigen
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            {imageUrl && (
              <div className="border-border bg-card max-h-[40dvh] w-full overflow-hidden rounded-xl border shadow-sm md:max-h-[calc(100vh_-_100px)] md:flex-1">
                <img
                  src={imageUrl}
                  alt={selected?.model?.name ?? selected?.name}
                  className="h-full w-full object-contain p-4"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
